import {
  ClockCircleOutlined,
  FacebookOutlined,
  InstagramOutlined,
} from '@ant-design/icons';
import {
  DownloadIcon,
  ShareIcon as HeroIconShareIcon,
  XIcon,
} from '@heroicons/react/outline';
import { ChevronLeftIcon } from '@heroicons/react/solid';
import { Button, Dropdown, Empty, MenuProps, Modal, Typography } from 'antd';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import { chapterPlaceholder } from 'assets';
import CopyClipBoard from 'assets/Icons/CopyClipBoard';
import LinkedInIcon from 'assets/Icons/LinkedInIcon';
import WhatsAppIcon from 'assets/Icons/WhatsAppIcon';
import clsx from 'clsx';
import { motion } from 'framer-motion';
import moment from 'moment';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';

import { useSessionTracking } from '@/hooks/useSessionTracking';

import RecommendedBooksSection from '@/components/BookLandingPageComp/RecommendedBooksSection';
import ShareButton from '@/components/PdfReader/ShareButton';
import Breadcrumbs from '@/components/shared/BreadCrunms';

import { user } from '@/atoms/user';
import { EventV2 } from '@/utils/Events/events';
import { User } from '@/utils/interfaces/User';

import { formatCount } from '../../ResourcesPage/ChaptersPage';
import { tokenUpdate } from '../../../../atoms/authModalControl';
import { updateFollow } from '../../../../services/School';
import ButtonDefault, { ButtonVariants } from '../../../shared/basic/button';
import ParserV2 from '../../../shared/basic/Parser/ParserV2';
import { ShouldRender } from '../../../shared/basic/ShouldRender';
import { getLoginToken } from '../../../shared/getToken';
import CustomLoader from '../../../shared/Loading';

interface Chapter {
  number: string;
  title: string;
  description: string;
  slug: string;
  postId: number;
  schoolId?: number;
  reading_history: [];
  postImage: string;
  reading_time: string;
}

export interface SchoolDetailsPageProps {
  schoolData: {
    schoolId: number;
    imageSrc: string;
    candidateImageSrc: string;
    candidateName: string;
    title: string;
    description: string;
    downloadPdfButtonText: string;
    chapters: Chapter[];
    last_read_post_slug: string;
    categoriesId: string;
    categoriesName?: string;
    slug?: string;
    authorUserName?: string;
    created_at?: string;
    reading_time?: string;
    authorAvatar?: string;
    authorData?: {
      name: string;
      readersCount: number;
      image: string;
      isFollowing: boolean;
      authorId: string;
      authorUserName?: string;
      authorAvatar?: string;
    };
  };
  loading: boolean;
}

const SchoolDetailsPage: React.FC<SchoolDetailsPageProps> = ({
  schoolData,
  loading,
}) => {
  const [token, setToken] = useState('');
  const userData: User | undefined = useRecoilValue(user);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const screens = useBreakpoint();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isFollowing, setIsFollowing] = useState(false);
  const [isTokenChanged, setIsTokenChanged] = useRecoilState(tokenUpdate);
  const [followCount, setFollowCount] = useState(0);
  const [isCopied, setIsCopied] = useState(false);
  const router = useRouter();
  const handleOk = () => {
    setIsModalOpen(false);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  const handleFollow = async ({
    author_id,
    is_follower,
  }: {
    author_id: string;
    is_follower: boolean;
  }) => {
    try {
      await updateFollow({
        author_id,
        is_follower,
      });
    } catch (error) {
      console.error('Error :', error);
    }
  };

  const toggleFollow = () => {
    const newState = !isFollowing;

    setIsFollowing(newState);
    setFollowCount((count) => (newState ? count + 1 : count - 1));

    handleFollow({
      author_id: schoolData.authorData?.authorId as string,
      is_follower: newState,
    });
  };

  const fetchData = async () => {
    try {
      const token = await getLoginToken();
      setToken(token as string);
    } catch (error) {
      console.error('Error fetching token:', error);
    }
  };

  useEffect(() => {
    fetchData();
    if (isTokenChanged) {
      setIsTokenChanged(false);
    }
  }, [isTokenChanged]);
  let maxWords;
  if (screens.md && !screens.xxl) {
    maxWords = 40;
  } else if (screens.xxl) {
    maxWords = undefined;
  } else {
    maxWords = undefined;
  }

  const shareOnPlatform = (platform: string, e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (typeof window === 'undefined') return;
    const url = encodeURIComponent(
      `${'https://zebralearn.com/dl/'}${router.asPath}`,
    );
    const isMobile = /Mobi|Android/i.test(navigator.userAgent);

    let shareUrl = '';

    switch (platform) {
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/shareArticle?mini=true&url=${url}`;
        break;
      case 'whatsapp':
        shareUrl = isMobile
          ? `whatsapp://send?text=${url}`
          : `https://web.whatsapp.com/send?text=${url}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${url}`;
        break;
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${url}`;
        break;
      case 'instagram':
        alert('Instagram sharing is only available via the mobile app.');
        return;
      default:
        return;
    }

    window.open(shareUrl, '_blank');
  };

  const handleCopyLink = () => {
    const linkToCopy = `${'https://zebralearn.com/dl/'}${router.asPath}`;

    navigator.clipboard
      .writeText(linkToCopy)
      .then(() => {
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 1000);
      })
      .catch((error) => {
        console.error('Failed to copy text:', error);
      });
  };


  // Events
  useEffect(() => {
    if (schoolData?.schoolId && schoolData?.title && schoolData?.categoriesName && userData)
      EventV2({
        payload: {
          event_name: 'details_view',
          source: 'schools',
          source_type: 'schools',
          source_id: schoolData?.schoolId?.toString(),
          source_name: schoolData.title,
          unit: 'boolean',
          value: 1,
          meta: {
            product_category: schoolData.categoriesName
          }
        },
        userData: userData,
        pathname: router.pathname
      })
  }, [userData])

  // details_session event — tracks active seconds, fires automatically when user leaves
  useSessionTracking({
    enabled: !!userData,
    userData,
    payload: {
      event_name: 'details_session',
      source: 'schools',
      source_type: 'schools',
      source_id: schoolData?.schoolId?.toString(),
      source_name: schoolData.title,
      meta: {
        product_category: schoolData.categoriesName
      }
    }
  })

  const items: MenuProps['items'] = [
    {
      key: '1',
      label: (
        <div
          className='flex gap-x-3 items-center cursor-pointer p-1 '
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleCopyLink();
          }}
        >
          <motion.span
            initial={{ scale: 1 }}
            animate={{ scale: isCopied ? 1.2 : 1 }}
          >
            <CopyClipBoard />
          </motion.span>

          <motion.span initial={{ scale: 1 }} className='text-white'>
            {isCopied ? (
              'Copied!'
            ) : (
              <span className='text-white z-20'>Copy Link</span>
            )}
          </motion.span>
        </div>
      ),
    },
    {
      type: 'divider',
    },
    {
      key: '2',
      label: (
        <div
          onClick={(e) => shareOnPlatform('facebook', e)}
          className='flex gap-x-3 items-center p-1'
        >
          <span>
            <FacebookOutlined className='text-xl translate-y-[2px]' />{' '}
          </span>
          Facebook
        </div>
      ),
    },
    {
      key: '3',
      label: (
        <div
          onClick={(e) => shareOnPlatform('instagram', e)}
          className='flex gap-x-3 items-center p-1'
        >
          <span>
            <InstagramOutlined className='text-xl translate-y-[2px]' />{' '}
          </span>
          Instagram
        </div>
      ),
    },
  ];

  return (
    <>
      {loading ? (
        <div className='flex justify-center h-screen items-center'>
          <CustomLoader />
        </div>
      ) : (
        <>
          <div className='flex flex-col   items-center justify-center'>
            <ShouldRender check={!screens.lg}>
              <div className='flex items-center justify-between gap-1 self-stretch py-2 pl-2 pr-4'>
                <button
                  onClick={() => router.push('/schools')}
                  className='flex  items-center'
                >
                  <ChevronLeftIcon className='w-6 h-6' />
                  <div className='back text-white  text-xs font-medium leading-5'>
                    Back
                  </div>
                </button>

                <ShareButton isLoggedIN={!!token} type='school' />
              </div>
            </ShouldRender>
            <span className='w-full bg-grayZl-950 flex justify-start md:justify-between pl-4 pt-2  md:py-1 md:pt-1 px-[4%] items-center'>
              <div className='lg:ml-[4%]'>
                <Breadcrumbs />
              </div>
              <ShouldRender check={token && screens.lg}>
                <ShareButton isLoggedIN={!!token} type='school' />
              </ShouldRender>
            </span>
            <div className='   flex flex-col w-full  px-4 lg:w-[93%]'>
              <div className='flex flex-col '>
                <div className='flex flex-col lg:flex-row items-center mt-6 md:mt-9 xl:mt-12 lg:items-start w-full  md:gap-7 lg:gap-10 xl:gap-12'>
                  <div className='w-[200px] md:[240px] lg:w-[320px] aspect-[1/1]    h-auto rounded-lg overflow-hidden relative flex-shrink-0'>
                    <ShouldRender check={schoolData.imageSrc}>
                      <Image
                        alt='School Image'
                        src={`${process.env.NEXT_PUBLIC_API_URL_IMAGE_BASE_URL_SCHOOLS}/${schoolData.imageSrc}`}
                        fill
                        className='object-cover'
                        priority={true}
                      />
                    </ShouldRender>
                  </div>

                  <div className=' mt-8 md:mt-0'>
                    <ShouldRender check={schoolData?.categoriesName}>
                      <div className='flex justify-center items-center text-sm md:text-base  min-w-[30px] py-[1px] md:py-1 px-3 rounded border border-[#ffffff]/[.10] bg-white/[.10] text-[#c7c6cf] w-fit font-medium leading-6'>
                        {schoolData?.categoriesName}
                      </div>
                    </ShouldRender>

                    <Typography.Title
                      level={1}
                      className='text-white text-lg lg:text-2xl xl:text-4xl font-semibold my-3 leading-tight'
                    >
                      {schoolData.title}
                    </Typography.Title>
                    <p className='text-[#C7C6CF] text-sm font-light md:text-base leading-relaxed mb-4 '>
                      <ParserV2
                        maxWords={maxWords}
                        text={schoolData.description}
                      />
                    </p>
                    <span className=' text-sm  flex items-center gap-x-2 '>
                      By <span className=' underline'>ZebraLearn</span>{' '}
                      <ShouldRender check={followCount}>
                        <span className='bg-dropDownMenuBg px-3 py-[2px] flex justify-center items-center rounded-full'>
                          {formatCount(followCount as number).formatted}{' '}
                          Learners
                        </span>
                      </ShouldRender>
                      <div className='text-sm flex gap-x-1'>
                        Updated on{' '}
                        {moment(schoolData?.created_at).format('D MMM, YYYY')}
                      </div>
                    </span>

                    <div className='flex justify-between items-center border-dropDownMenuBg py-3 mt-6 mb-4 md:my-6 border-y'>
                      <span className='flex gap-x-2 text-white text-sm'>
                        <ClockCircleOutlined />{' '}
                        <ShouldRender check={screens.md}>
                          <p className='text-[#9F9EA6]'>
                            Total Reading Time:
                          </p>{' '}
                        </ShouldRender>
                        {`${Math.round((Number(schoolData?.reading_time) || 0) / 60)} Minutes`}
                      </span>
                      <div className='flex space-x-2 z-20'>
                        <button
                          style={{
                            background:
                              'linear-gradient(360deg, #252525 0%, #545454 100%)',
                          }}
                          className=' text-white pl-2 bg-opacity-70 pr-4  h-8 flex gap-x-2 items-center text-[10px] rounded-full'
                          onClick={(e) => shareOnPlatform('linkedin', e)}
                        >
                          <span className='  flex justify-center gap-x-1.5 -translate-y-[1.5px] md:-translate-y-[1px]'>
                            <LinkedInIcon /> <p className='pt-[3px]'>share</p>
                          </span>
                        </button>

                        <button
                          className=' text-white p-1 bg-[#222326] bg-opacity-70 h-8 flex gap-x-2 items-center text-[10px] rounded-full'
                          onClick={(e) => shareOnPlatform('whatsapp', e)}
                        >
                          <span className='  flex justify-center gap-x-1.5 -translate-y-[1px]'>
                            <WhatsAppIcon />
                          </span>
                        </button>
                        <Dropdown
                          menu={{ items }}
                          arrow={true}
                          placement='bottomLeft'
                        >
                          <button
                            className=' text-white p-1 bg-[#222326] bg-opacity-70 w-8 h-8 flex gap-x-2 items-center text-[10px] rounded-full'
                            onClick={(e) => shareOnPlatform('', e)}
                          >
                            <span className='  flex justify-center gap-x-1.5 -translate-y-[1px]'>
                              <HeroIconShareIcon
                                className='w-5 h-5 text-[#AAAAAA]'
                                style={{ strokeWidth: '1px' }}
                              />
                            </span>
                          </button>
                        </Dropdown>
                      </div>
                    </div>
                    <ShouldRender
                      check={token && schoolData?.last_read_post_slug}
                    >
                      <span className='flex'>
                        <Link
                          href={`/schools/${schoolData?.slug}/${schoolData?.last_read_post_slug}/?school_id=${schoolData?.schoolId}`}
                          className='w-full lg:w-min'
                        >
                          <ButtonDefault
                            size={2}
                            variant={ButtonVariants.WHITE}
                            className='w-full lg:w-min md:p-1 xl:font-semibold'
                            rounded={false}
                            roundedSm={true}
                          >
                            <span className='flex text-sm md:text-base   rounded-md w-full  md:py-1  gap-x-2  px-2 md:px-4  whitespace-nowrap'>
                              Continue Reading
                            </span>
                          </ButtonDefault>
                        </Link>
                      </span>
                    </ShouldRender>
                  </div>
                </div>
              </div>

              <div className='flex items-center gap-2 my-7 lg:my-10 xl:my-12'>
                <div className=' text-white text-base lg:text-xl xl:text-2xl font-semibold leading-8'>
                  Chapters
                </div>
                <div className='flex flex-col justify-center items-center gap-2.5 md:py-1 px-3 rounded-full bg-[#ff5612]/[.20]  text-[#ff5612] text-base lg:text-xl xl:text-2xl font-medium leading-8'>
                  {String(schoolData?.chapters?.length).padStart(2, '0')}
                </div>
              </div>

              <div className='grid grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-4 md:gap-8'>
                {schoolData?.chapters?.map((chapter, index) => {
                  return (
                    <Link
                      href={`/schools/${schoolData?.slug}/${chapter?.slug}/?school_id=${chapter?.schoolId}&post_id=${chapter?.postId}`}
                      key={index}
                    >
                      <div className='flex items-center justify-center mb-3 gap-x-3'>
                        <div className='text-lg lg:text-[32px] font-light text-white '>
                          {String(index + 1).padStart(2, '0')}
                        </div>
                        <div
                          className={clsx(
                            !chapter?.reading_history
                              ? 'bg-[#282829]'
                              : 'bg-primary',
                            'h-[2px] md:h-1 rounded-full w-full ',
                          )}
                        ></div>
                      </div>

                      <div className='w-full h-24 md:h-[200px] rounded-sm md:rounded-xl   mb-4 relative overflow-hidden'>
                        <Image
                          alt={chapter?.title || 'Chapter Image'}
                          src={
                            chapter?.postImage
                              ? `${process.env.NEXT_PUBLIC_API_URL_IMAGE_BASE_URL_SCHOOLS}/${chapter?.postImage}`
                              : chapterPlaceholder
                          }
                          fill
                          priority={true}
                        />
                      </div>
                      <ShouldRender check={chapter?.reading_time}>
                        <div className='mb-2'>
                          <span className='text-[#ff6b35] text-sm font-medium'>
                            {`${Math.round((Number(chapter?.reading_time) || 0) / 60)} Minutes`}
                          </span>
                        </div>
                      </ShouldRender>

                      <h3 className='text-white text-sm md:text-lg font-medium leading-relaxed '>
                        {chapter.title}
                      </h3>
                    </Link>
                  );
                })}
              </div>

              <ShouldRender check={schoolData?.chapters?.length === 0}>
                <Empty
                  description={
                    <span className='text-white pt-5'>No Chapters</span>
                  }
                />
              </ShouldRender>
            </div>
          </div>
          <div className='px-4 lg:w-[93%] mt-10 mx-auto'>
            <RecommendedBooksSection
              category={schoolData?.categoriesName ?? undefined}
              token={token}
              type='school'
            />
          </div>
        </>
      )}

      <Modal
        closable={false}
        open={isModalOpen}
        className='customMaskModal rounded-2xl'
        onOk={handleOk}
        footer={null}
        width={300}
        onCancel={handleCancel}
      >
        <div className='bg-[#121212] p-5 text-white border rounded-2xl border-[#3D3D3D]  '>
          <span
            onClick={handleCancel}
            className='w-full flex justify-end cursor-pointer hover:text-gray-300'
          >
            <XIcon className='w-6 h-6' />
          </span>
          <div className='w-full flex justify-center py-7'>
            <div className='flex flex-col gap-y-4'>
              <div className='flex justify-center'>
                <span className='bg-[#121212] p-4 flex w-fit rounded-full justify-center'>
                  <DownloadIcon className='w-8 h-8' />{' '}
                </span>
              </div>
              <div className='flex flex-col gap-y-2'>
                <span className='text-[#A0A0A0] font-medium text-sm text-center'>
                  {schoolData?.chapters?.length} Chapters
                </span>
                <span className='text-white font-medium text-sm text-center'>
                  {schoolData.title}
                </span>
              </div>

              <span className='text-[#A0A0A0] font-medium text-sm text-center mt-5'>
                <ButtonDefault
                  size={1}
                  variant={ButtonVariants.WHITE}
                  className='w-min p-1 rounded-full bg-[#3D3D3D] hover:bg-[#232323] text-white border-none'
                >
                  <span className='flex text-xs gap-x-2 px-4 py-1 whitespace-nowrap'>
                    Download {schoolData?.chapters?.length} chapters
                  </span>
                </ButtonDefault>
              </span>
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        open={isModalVisible}
        // onOk={() => }
        onCancel={() => setIsModalVisible(!isModalVisible)}
        footer={null} // Remove default footer
        width='400px'
        className='bg-[#212121] rounded-lg'
        closable={false}
        centered={true}
        destroyOnHidden
      >
        <div className='flex flex-col'>
          <div className='py-5'>
            Sure want to Un-Follow {schoolData?.authorData?.name} ?
          </div>
          <div className='flex gap-x-1 justify-end'>
            <span>
              <Button
                onClick={() => setIsModalVisible(!isModalVisible)}
                type='text'
              >
                No
              </Button>
            </span>
            <span>
              <Button
                onClick={() => {
                  toggleFollow();
                  setIsModalVisible(false);
                }}
                type='text'
                className='text-blue-500 font-medium'
              >
                Yes
              </Button>
            </span>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default SchoolDetailsPage;
